import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { cn } from '@/shared/lib/cn';
import type { PlanResponseDTO } from '../types';

interface PlanCardProps {
  plan: PlanResponseDTO;
  showActions?: boolean;
  onDelete?: (id: string) => void;
  isDeleting?: boolean;
}

export function PlanCard({ plan, showActions, onDelete, isDeleting }: PlanCardProps) {
  const { t } = useTranslation();

  return (
    <Card className="relative">
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-2">
          <div>
            <h3 className="font-semibold text-foreground">{plan.name_en}</h3>
            <p className="text-xs text-muted-foreground font-mono">{plan.slug}</p>
          </div>
          <span
            className={cn(
              'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
              plan.is_active
                ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
            )}
          >
            {plan.is_active ? t('plans.isActive') : t('products.status.inactive')}
          </span>
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        <div className="flex items-baseline gap-1">
          <span className="text-2xl font-bold text-foreground">
            {(plan.price_amount / 100).toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </span>
          <span className="text-sm text-muted-foreground">{plan.currency}</span>
        </div>
        <p className="text-xs text-muted-foreground">{t(`plans.periods.${plan.billing_period}`)}</p>
        {plan.trial_days > 0 && (
          <p className="text-xs text-blue-600 dark:text-blue-400">
            {t('plans.trialDays', { count: plan.trial_days })}
          </p>
        )}
        {showActions && onDelete && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="absolute bottom-2 start-2 h-8 w-8 text-muted-foreground hover:text-destructive"
            onClick={() => onDelete(plan.id)}
            disabled={isDeleting}
          >
            ×
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
